import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as countActions from "@rentiohq/shared-frontend/dist/redux/count/count.actions";
import * as countSelectors from "@rentiohq/shared-frontend/dist/redux/count/count.selectors";
import * as paymentActionsV1 from "@rentiohq/shared-frontend/dist/redux/payment/payment.actions";
import * as paymentSelectorsV1 from "@rentiohq/shared-frontend/dist/redux/payment/payment.selectors";
import * as rentDepositActions from "@rentiohq/shared-frontend/dist/redux/rentDeposit/rentDeposit.actions";
import * as rentDepositSelectors from "@rentiohq/shared-frontend/dist/redux/rentDeposit/rentDeposit.selectors";
import * as templateSelectors from "@rentiohq/shared-frontend/dist/redux/template/template.selectors";
import * as templateUtils from "@rentiohq/shared-frontend/dist/redux/template/template.utils";
import * as templateDocumentActions from "@rentiohq/shared-frontend/dist/redux/templateDocument/templateDocument.actions";
import * as templateDocumentSelectors from "@rentiohq/shared-frontend/dist/redux/templateDocument/templateDocument.selectors";
import * as templateDocumentUtils from "@rentiohq/shared-frontend/dist/redux/templateDocument/templateDocument.utils";
import * as contractActions from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.actions";
import * as documentPackageActions from "@rentiohq/shared-frontend/dist/reduxV2/documentPackage/documentPackage.actions";
import * as documentPackageSelectors from "@rentiohq/shared-frontend/dist/reduxV2/documentPackage/documentPackage.selectors";
import * as paymentOrderActionsV2 from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.actions";
import * as paymentOrderSelectorsV2 from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.selectors";
import * as paymentOrderUtils from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.utils";
import { IExtraChecklistItem } from "@rentiohq/shared-frontend/dist/reduxV2/preferences";
import * as rentRegistrationActions from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.actions";
import * as rentRegistrationSelectors from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.selectors";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import {
  IContract,
  EInsuranceExternalStatus,
} from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  EDocumentPackageStatus,
  EDocumentPackageTypes,
} from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import { EPaymentOrderType } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { EPreferences } from "@rentiohq/shared-frontend/dist/types/preferences.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { ERentDepositStatus } from "@rentiohq/shared-frontend/dist/types/rentDeposit.types";
import { appendWhere } from "@rentiohq/shared-frontend/dist/utils/api.utils";
import { usePrevious } from "@rentiohq/shared-frontend/dist/utils/hooks.utils";
import { Card, ESpacings } from "@rentiohq/web-shared/dist/components";
import { ELocationDescriptionType } from "forms/orderLocationDescription/schema.orderLocationDescription.types";
import { requestInsuranceValidationErrors } from "forms/requestInsurance/schema.requestInsurance.utils";
import { compact } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import { NumberParam, StringParam } from "serialize-query-params";
import { LocationDescriptionModal } from "../../../../../../../../components/LocationDescriptionModal";
import RequestInsuranceModal from "../../../../../../../../components/RequestInsuranceModal";
import { RequestRentioMoveServiceModal } from "../../../../../../../../components/RequestRentioMoveServiceModal";
import TemplateDocumentContractCreateModal from "../../../../../../../../components/TemplateDocumentContractCreateModal";
import { AdministrationItems } from "../AdministrationItems";
import { useGetAdministrationEntryItems } from "../AdministrationItems/useGetAdministrationItems";
import { EActionStatus, IChecklistItem } from "../ChecklistItem";
import { ChecklistItems } from "../ChecklistItems";
import { useGetEntryChecklistItems } from "../ChecklistItems/useGetEntryChecklistItems";

interface IProps {
  contract: IContract;
  property: IProperty;
  showCircles: boolean;
  showChecklistItems: boolean;
  setShowChecklistItems: (show: boolean) => void;
}

export const EntryAdministration = ({
  contract,
  property,
  showCircles,
  showChecklistItems,
  setShowChecklistItems,
}: IProps) => {
  const dispatch = useDispatch();
  const { isBroker } = authHooks.useSelf();

  const shouldFetchData = showCircles || showChecklistItems;

  const rentDepositsIdentifier = `rent-deposits-contract-${contract.id}`;
  const paymentOrdersIdentifier = `contract-${contract.id}`;
  const contractsPaymentOrdersCountIdentifier = `contracts-payment-orders-count-${contract.id}`;

  const hasInsuranceForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.Insurance,
  );

  const hasPaymentV2ForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.PaymentV2,
  );

  const [queryParams, setQueryParamValue] = useQueryParams({
    documentId: StringParam,
    templateDocumentId: StringParam,
    documentPackageId: StringParam,
    rentDepositId: NumberParam,
    insuranceContractId: StringParam,
    requestInsuranceContractId: StringParam,
    moveRequestId: NumberParam,
    registrationId: NumberParam,
  });

  // Local state
  const [
    showTemplateDocumentContractCreate,
    setShowTemplateDocumentContractCreate,
  ] = React.useState(false);
  const [showLocationDescriptionModal, setShowLocationDescriptionModal] =
    React.useState(false);
  const [showRequestInsuranceModal, setShowRequestInsuranceModal] =
    React.useState(false);
  const [didShowAutomaticInsuranceModal, setDidShowAutomaticInsuranceModal] =
    React.useState(false);
  const [showRequestRentioMoveService, setShowRequestRentioMoveService] =
    React.useState(false);

  // Registrations
  const registration = useSelector((state: IRootStore) =>
    rentRegistrationSelectors.getDetail(state, contract?.rentRegistrationId),
  );

  const isFetchingRegistration = useSelector((state: IRootStore) =>
    rentRegistrationSelectors.isFetchingDetail(
      state,
      contract?.rentRegistrationId,
    ),
  );

  // Rent deposits
  const rentDeposits = useSelector((state: IRootStore) =>
    rentDepositSelectors.getRentDeposits(state, rentDepositsIdentifier),
  );

  const isFetchingRentDeposits = useSelector((state: IRootStore) =>
    rentDepositSelectors.isFetchingRentDeposits(state, rentDepositsIdentifier),
  );

  // Payment order V1
  const paymentOrdersV1 = useSelector((state: IRootStore) =>
    paymentSelectorsV1.getPaymentOrdersByIdentifier(
      state,
      paymentOrdersIdentifier,
    ),
  );

  const isFetchingPaymentOrdersV1 = useSelector((state: IRootStore) =>
    paymentSelectorsV1.isFetchingPaymentOrdersByIdentifier(
      state,
      paymentOrdersIdentifier,
    ),
  );

  // Payment order V2

  const filter = {
    and: [
      { contractId: { eq: contract.id } },
      { completedAt: { is: null } },
      {
        type: {
          in: [
            EPaymentOrderType.Rent,
            EPaymentOrderType.CommonCosts,
            EPaymentOrderType.NonCommonCosts,
          ],
        },
      },
    ],
  };

  const paymentOrdersV2 = useSelector(
    (state: IRootStore) =>
      paymentOrderSelectorsV2.getPaged(
        state,
        paymentOrderUtils.getPaymentOrdersQuery({
          filter,
        }),
      )?.items,
  );

  const isFetchingPaymentOrdersV2 = useSelector(
    (state: IRootStore) =>
      paymentOrderSelectorsV2.getPaged(
        state,
        paymentOrderUtils.getPaymentOrdersQuery({
          filter,
        }),
      )?.isFetching,
  );

  // Document Packages
  const query = {
    filter: {
      and: [
        {
          contractId: { eq: contract.id },
          type: { eq: EDocumentPackageTypes.Contract },
          status: {
            notIn: [
              EDocumentPackageStatus.Revoked,
              EDocumentPackageStatus.Expired,
              EDocumentPackageStatus.Failed,
              EDocumentPackageStatus.Rejected,
              EDocumentPackageStatus.WorkerFailed,
            ],
          },
        },
      ],
    },
  };

  const documentPackages = useSelector(
    (state: IRootStore) =>
      documentPackageSelectors.getAllWhere(state, query)?.items,
  );

  const isFetchingDocumentPackages = useSelector((state: IRootStore) =>
    documentPackageSelectors.isFetchingAllWhere(state, query),
  );

  // Payment orders Count
  const paymentOrdersCount =
    useSelector((state: IRootStore) =>
      countSelectors.getCount(state, contractsPaymentOrdersCountIdentifier),
    ) || 0;

  const isFetchingPaymentOrdersCount = useSelector((state: IRootStore) =>
    countSelectors.getIsFetchingCount(
      state,
      contractsPaymentOrdersCountIdentifier,
    ),
  );

  // Templates
  const templates = useSelector((state: IRootStore) =>
    templateSelectors.paged.dataForPage(state, {
      id: templateUtils.getPagedId({}),
      page: 0,
    }),
  );

  const isFetchingTemplates = useSelector((state: IRootStore) =>
    templateSelectors.paged.isFetchingPage(state, {
      id: templateUtils.getPagedId({}),
      page: 0,
    }),
  );

  // Template documents
  const contractTemplateDocumentsFilter = {
    where: {
      contractId: contract.id,
      type: { like: "%contract%", options: "i" },
    },
  };

  const contractTemplateDocuments = useSelector((state: IRootStore) =>
    templateDocumentSelectors.paged.dataForPage(state, {
      id: templateDocumentUtils.getPagedId({
        filter: contractTemplateDocumentsFilter,
      }),
      page: 0,
    }),
  );

  const isFetchingContractTemplateDocuments = useSelector((state: IRootStore) =>
    templateDocumentSelectors.paged.isFetchingPage(state, {
      id: templateDocumentUtils.getPagedId({
        filter: contractTemplateDocumentsFilter,
      }),
      page: 0,
    }),
  );

  // Automatically show request insurance modal after opening rent deposit
  React.useEffect(() => {
    if (isBroker && !hasInsuranceForBroker) {
      return;
    }

    if (didShowAutomaticInsuranceModal) {
      return;
    }

    if (!queryParams.requestInsuranceContractId) {
      return;
    }

    if (contract.id !== queryParams.requestInsuranceContractId) {
      return;
    }

    if (contract.insuranceExternalStatus !== EInsuranceExternalStatus.None) {
      return;
    }

    const validationErrors = requestInsuranceValidationErrors({
      property,
      contract,
    });

    if (validationErrors.length > 0) {
      return;
    }

    setShowRequestInsuranceModal(true);
    setDidShowAutomaticInsuranceModal(true);
  }, [
    contract.id,
    hasInsuranceForBroker,
    queryParams.requestInsuranceContractId,
  ]);

  // Registration
  React.useEffect(() => {
    if (
      !shouldFetchData ||
      isFetchingRegistration ||
      !contract?.rentRegistrationId
    ) {
      return;
    }

    dispatch(
      rentRegistrationActions.getDetailStart.getAction({
        id: contract?.rentRegistrationId,
      }),
    );
  }, [contract?.rentRegistrationId, shouldFetchData]);

  // Refresh the contract whenever a registration drawer/flow tied to this
  // contract is opened or closed (e.g. after starting a registration, a manual
  // registration, or an addendum). The contract entity is shared with the list
  // selector, so refetching it keeps the registration checklist item in sync
  // with the latest `rentRegistrationId` / `manuallyCompletedRegistration`.
  const previousRegistrationId = usePrevious(queryParams.registrationId);

  React.useEffect(() => {
    if (queryParams.registrationId === previousRegistrationId) {
      return;
    }

    const relatesToThisContract =
      queryParams.registrationId === contract.rentRegistrationId ||
      previousRegistrationId === contract.rentRegistrationId ||
      contract.rentRegistrationId == null;

    if (!relatesToThisContract) {
      return;
    }

    dispatch(contractActions.getDetailStart.getAction({ id: contract.id }));
  }, [
    queryParams.registrationId,
    previousRegistrationId,
    contract.id,
    contract.rentRegistrationId,
    dispatch,
  ]);

  // Template documents
  React.useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    if (isFetchingContractTemplateDocuments) {
      return;
    }

    dispatch(
      templateDocumentActions.getPaged.actions.start({
        page: 0,
        // limit: 100,
        filter: contractTemplateDocumentsFilter,
      }),
    );
  }, [contract.id, shouldFetchData]);

  // Payment orders V2
  React.useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    if (!hasPaymentV2ForBroker) {
      return;
    }

    if (contract.manuallyCompletedPaymentRent) {
      return;
    }

    if (isFetchingPaymentOrdersV2) {
      return;
    }

    dispatch(
      paymentOrderActionsV2.getPagedStart.getAction({
        query: paymentOrderUtils.getPaymentOrdersQuery({
          filter,
        }),
      }),
    );
  }, [contract.id, shouldFetchData]);

  // Payment orders V1
  React.useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    if (hasPaymentV2ForBroker) {
      return;
    }

    if (contract.manuallyCompletedPaymentRent) {
      return;
    }

    if (isFetchingPaymentOrdersV1) {
      return;
    }

    dispatch(
      paymentActionsV1.getPaymentOrdersByIdentifier.actions.start({
        paymentOrdersIdentifier,
        refetch: true,
        filterData: {
          where: { contractId: contract.id, completedAt: null },
        },
      }),
    );
  }, [contract.id, shouldFetchData]);

  // Document packages
  React.useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    if (contract.manuallyCompletedDocumentSigning) {
      return;
    }

    if (isFetchingDocumentPackages) {
      return;
    }

    if (documentPackages && documentPackages.length > 0) {
      return;
    }

    dispatch(
      documentPackageActions.getAllWhereStart.getAction({
        query,
      }),
    );
  }, [contract.id, shouldFetchData]);

  // Rent deposits
  React.useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    if (contract.manuallyCompletedRentDeposit) {
      return;
    }

    if (isFetchingRentDeposits) {
      return;
    }

    dispatch(
      rentDepositActions.getRentDeposits.actions.start({
        identifier: rentDepositsIdentifier,
        refetch: true,
        extraFilterData: {
          where: {
            basicContractId: contract.id,
            status: { neq: ERentDepositStatus.Cancelled },
          },
          order: "createdAt DESC",
        },
        limit: 1,
      }),
    );
  }, [contract.id, shouldFetchData]);

  // Payment orders count
  React.useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    if (isFetchingPaymentOrdersCount) {
      return;
    }

    dispatch(
      countActions.getCount.actions.start({
        countIdentifier: contractsPaymentOrdersCountIdentifier,
        countBase: appendWhere("/payment-orders/count", {
          and: [{ contractId: contract.id }, { type: EPaymentOrderType.Rent }],
        }),
      }),
    );
  }, [contract.id, shouldFetchData]);

  const entryExtraActions = React.useMemo(() => {
    const entry = contract.extraChecklist?.find(
      e => e.key === EPreferences.ChecklistExtraEntry,
    );
    if (!entry || entry.actions.length === 0) return undefined;
    return [...entry.actions].sort((a, b) => a.index - b.index);
  }, [contract.extraChecklist]);

  const updateActionCompletionStatus = (action: IExtraChecklistItem) => {
    if (!entryExtraActions) return undefined;
    dispatch(
      contractActions.updateExtraChecklistItemStatusStart.getAction({
        contractId: contract.id,
        data: {
          key: EPreferences.ChecklistExtraEntry,
          actions: entryExtraActions.map((a: IExtraChecklistItem) =>
            a.index === action.index ? { ...a, completed: !a.completed } : a,
          ),
        },
      }),
    );
  };

  const entryExtraChecklistItems: IChecklistItem[] | undefined =
    React.useMemo(() => {
      if (!entryExtraActions) return undefined;
      return entryExtraActions.map(action => ({
        actionStatus: action.completed
          ? EActionStatus.CompletedManually
          : EActionStatus.Incomplete,
        title: action.title,
        content: action.description,
        onClick: () => updateActionCompletionStatus(action),
        onChangeActionStatus: () => updateActionCompletionStatus(action),
      }));
    }, [entryExtraActions, contract.id]);

  const entryChecklistItems = useGetEntryChecklistItems({
    contract,
    property,
    registration,
    templates,
    contractTemplateDocuments,
    isFetchingTemplates,
    isFetchingContractTemplateDocuments,
    documentPackages,
    isFetchingDocumentPackages,
    rentDeposits,
    isFetchingRentDeposits,
    paymentOrdersCount,
    isFetchingPaymentOrdersCount,
    // Callbacks
    onCreateContractFromTemplateDocument: () => {
      setShowTemplateDocumentContractCreate(true);
    },
    onShowDocument: documentId => {
      setQueryParamValue({
        documentId,
      });
    },
    onShowTemplateDocument: templateDocumentId => {
      setQueryParamValue({
        templateDocumentId,
      });
    },
    onShowDocumentPackage: documentPackageId => {
      setQueryParamValue({
        documentPackageId,
      });
    },
    onShowRentDeposit: rentDepositId => {
      setQueryParamValue({
        rentDepositId,
      });
    },
    onOrderPropertyInspectionReport: () => {
      setShowLocationDescriptionModal(true);
    },
    onRequestInsurance: () => {
      setShowRequestInsuranceModal(true);
    },
    onShowInsurance: (insuranceContractId: string) => {
      setQueryParamValue({
        insuranceContractId,
      });
    },
    onCreateRentioMoveRequest: () => {
      setShowRequestRentioMoveService(true);
    },
    onShowMoveRequest: (moveRequestId: number) => {
      setQueryParamValue({ moveRequestId });
    },
    onShowRentRegistration: (registrationId: number) => {
      setQueryParamValue({
        registrationId,
      });
    },

    // Config
    shouldFetchData: showChecklistItems,
  });

  const entryItems = useGetAdministrationEntryItems({
    contract,
    property,
    registration,
    checklistItems: entryChecklistItems,
    contractTemplateDocuments,
    // @ts-ignore
    rentDeposits,
    paymentOrdersV1,
    paymentOrdersV2,
    documentPackages,
    shouldFetchData: showChecklistItems,
    extraChecklistActions: entryExtraActions,
  });

  const handleLocationDescriptionModalClose = () => {
    setShowLocationDescriptionModal(false);
  };

  const handleClickRequestInsuranceModalClose = () => {
    setShowRequestInsuranceModal(false);
    setQueryParamValue({
      requestInsuranceContractId: undefined,
      insuranceContractId: undefined,
    });
  };

  const handleClickSeeInsuranceDetails = (insuranceContractId?: string) => {
    setShowRequestInsuranceModal(false);

    setQueryParamValue({
      requestInsuranceContractId: undefined,
      insuranceContractId,
    });
  };

  const handleClickRequestRentioMoveServiceModalClose = () => {
    setShowRequestRentioMoveService(false);
  };

  return (
    <>
      {entryItems?.length && (
        <Card.Section boxProps={{ p: ESpacings.none }}>
          <AdministrationItems
            items={entryItems}
            showCircles={showCircles}
            showChecklistItems={showChecklistItems}
            setShowChecklistItems={setShowChecklistItems}
            // checklistItems={compact(Object.values(entryChecklistItems))}
          />
        </Card.Section>
      )}

      {showChecklistItems && (
        <Card.Section
          boxProps={{
            p: ESpacings.none,
            mt: ESpacings.loose,
            pt: ESpacings.tight,
          }}
        >
          <ChecklistItems
            checklistItems={compact(Object.values(entryChecklistItems))}
            property={property}
            contract={contract}
            extraChecklistItems={entryExtraChecklistItems}
          />
        </Card.Section>
      )}

      {showTemplateDocumentContractCreate && (
        <TemplateDocumentContractCreateModal
          property={property}
          contract={contract}
          onClose={() => {
            setShowTemplateDocumentContractCreate(false);
          }}
          type={"contract"}
        />
      )}

      {showLocationDescriptionModal && (
        <LocationDescriptionModal
          property={property}
          contract={contract}
          onClose={handleLocationDescriptionModalClose}
          type={ELocationDescriptionType.In}
        />
      )}

      {showRequestInsuranceModal && (
        <RequestInsuranceModal
          property={property}
          contract={contract}
          onClose={handleClickRequestInsuranceModalClose}
          onViewDetails={handleClickSeeInsuranceDetails}
        />
      )}

      {showRequestRentioMoveService && (
        <RequestRentioMoveServiceModal
          property={property}
          contract={contract}
          onClose={handleClickRequestRentioMoveServiceModalClose}
          energyTransferByMexp={false}
        />
      )}
    </>
  );
};
